import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSettings } from '@/contexts/SettingsContext';
import { createLead, trackEvent } from '@/lib/api';
import { buildWhatsAppUrl } from '@/lib/utils';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle } from 'lucide-react';

export default function ContactPage() {
    const { settings } = useSettings();
    const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.message) {
            toast.error('Por favor, rellena al menos tu nombre y mensaje.');
            return;
        }
        setSending(true);
        try {
            await createLead({ ...formData, channel: 'form' });
            toast.success('Mensaje enviado. Te contactaremos pronto.');
            setSent(true);
            setFormData({ name: '', phone: '', email: '', message: '' });
        } catch (err) {
            toast.error('Error al enviar. Inténtalo de nuevo.');
        } finally {
            setSending(false);
        }
    };

    const contactInfo = [
        { icon: MapPin, label: 'Dirección', value: settings?.address || 'Avda. de Córdoba 15, 06400 Don Benito, Badajoz' },
        { icon: Phone, label: 'Teléfono', value: settings?.phone || '+34 600 000 000', href: `tel:${settings?.phone || '+34600000000'}` },
        { icon: Mail, label: 'Email', value: settings?.email || 'info@asgmotor.com', href: `mailto:${settings?.email || 'info@asgmotor.com'}` },
        { icon: Clock, label: 'Horario', value: settings?.schedule || 'L-V: 9:00-14:00 / 16:30-20:00 | S: 10:00-14:00' },
    ];

    return (
        <div className="py-10 sm:py-14" data-testid="contact-page">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <h1 className="font-[var(--font-display)] tracking-tight text-3xl sm:text-4xl font-bold text-[var(--brand-navy)] mb-2">
                    Contacto
                </h1>
                <p className="text-base text-[hsl(var(--muted-foreground))] mb-8">
                    ¿Tienes dudas? Escríbenos o llámanos, estamos encantados de ayudarte.
                </p>

                <div className="grid md:grid-cols-[1fr_1.2fr] gap-8">
                    {/* Contact Info + Map */}
                    <div className="space-y-5">
                        <Card className="p-5 rounded-xl">
                            <h2 className="font-[var(--font-display)] font-semibold text-sm mb-4">Información de contacto</h2>
                            <div className="space-y-4">
                                {contactInfo.map((c, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[var(--brand-light-blue)] shrink-0">
                                            <c.icon className="h-4 w-4 text-[var(--brand-blue)]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-0.5">{c.label}</p>
                                            {c.href ? (
                                                <a href={c.href} className="text-sm font-medium hover:text-[var(--brand-blue)] transition-colors duration-200">{c.value}</a>
                                            ) : (
                                                <p className="text-sm font-medium">{c.value}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <a
                            href={buildWhatsAppUrl(settings)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent({ event_type: 'whatsapp_click' }).catch(() => {})}
                        >
                            <Button className="w-full h-12 bg-[#25D366] text-white hover:bg-[#20BD59] text-base gap-2">
                                <MessageCircle className="h-5 w-5" />
                                Contactar por WhatsApp
                            </Button>
                        </a>

                        <div className="rounded-xl overflow-hidden border shadow-sm aspect-video" data-testid="contact-map">
                            <iframe
                                title="Ubicación ASG Motor"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3124.0!2d-5.861!3d38.955!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAvda+de+C%C3%B3rdoba+15%2C+06400+Don+Benito%2C+Badajoz!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                                className="w-full h-full border-0"
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>

                    {/* Contact Form */}
                    <Card className="p-5 sm:p-6 rounded-xl" data-testid="contact-form">
                        {sent ? (
                            <div className="text-center py-8" data-testid="contact-form-success-message">
                                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-[var(--brand-success)]" />
                                <h2 className="font-[var(--font-display)] font-semibold text-lg mb-2">¡Mensaje enviado!</h2>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Te contactaremos lo antes posible.</p>
                                <Button variant="outline" onClick={() => setSent(false)}>Enviar otro mensaje</Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <h2 className="font-[var(--font-display)] font-semibold text-lg mb-2">Envíanos un mensaje</h2>
                                <div>
                                    <Label htmlFor="name" className="text-sm">Nombre *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Tu nombre"
                                        className="mt-1"
                                        data-testid="contact-form-name-input"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="phone" className="text-sm">Teléfono</Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                            placeholder="+34 ..."
                                            className="mt-1"
                                            data-testid="contact-form-phone-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email" className="text-sm">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="tu@email.com"
                                            className="mt-1"
                                            data-testid="contact-form-email-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="message" className="text-sm">Mensaje *</Label>
                                    <Textarea
                                        id="message"
                                        value={formData.message}
                                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                        placeholder="Cuéntanos en qué podemos ayudarte..."
                                        className="mt-1 min-h-[120px]"
                                        data-testid="contact-form-message-textarea"
                                        required
                                    />
                                </div>
                                <Button type="submit" disabled={sending} className="w-full bg-[var(--brand-navy)] hover:bg-[#0d3560] text-white gap-2" data-testid="contact-form-submit-button">
                                    <Send className="h-4 w-4" />
                                    {sending ? 'Enviando...' : 'Enviar mensaje'}
                                </Button>
                            </form>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
